import classnames from 'classnames';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './FieldJournal.module.scss';

/** The empty field journal; its page areas are placed in the stylesheet. */
const JOURNAL_BACKGROUND_URL = '/assets/chronicle/journal-background.webp';

interface FieldJournalProps {
    left: ReactNode;
    right: ReactNode;
    /** The filling book's own root class; its page styles nest under it. */
    className?: string;
}

/**
 * The character's field journal lying open, handwritten throughout. The
 * Journal is written from the front and the Compendium in the back, so both
 * are this one book: the same paper, hand, ink and page parts, filled in.
 */
export function FieldJournal({ left, right, className }: FieldJournalProps): JSX.Element {
    return (
        <div className={classnames(styles.fieldJournal, className)}>
            <div className={styles.book}>
                <img className={styles.cover} src={JOURNAL_BACKGROUND_URL} alt="" draggable={false} />
                <div className={styles.leftPage}>{left}</div>
                <div className={styles.rightPage}>{right}</div>
            </div>
        </div>
    );
}

interface PageHeadingProps {
    title: ReactNode;
    /** A line under the title in faded ink: a date, who it is from, a page count. */
    meta?: ReactNode;
}

/** A page's handwritten title, centered. */
export function PageHeading({ title, meta }: PageHeadingProps): JSX.Element {
    return (
        <header className={styles.pageHeading}>
            <h3 className={styles.pageTitle}>{title}</h3>
            {meta !== undefined && <p className={styles.pageMeta}>{meta}</p>}
        </header>
    );
}

/** A written page: its parts down the page, anything signed at the foot (`margin-top: auto`). */
export function JournalPage({ children }: { children: ReactNode }): JSX.Element {
    return <article className={styles.page}>{children}</article>;
}

/** A note in faded ink where there is nothing written yet. */
export function PageNote({ children }: { children: ReactNode }): JSX.Element {
    return <p className={styles.pageNote}>{children}</p>;
}

/** A ribbon tab for what can be done with a page: Replay, Accept, Relive. */
export function JournalRibbon(props: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'type'>): JSX.Element {
    return <button type="button" className={styles.ribbon} {...props} />;
}
